import { FC, memo } from 'react';
import Header from './components/Header';
import SidePanel from './components/SidePanel';
import styled, { createGlobalStyle } from 'styled-components';
import { EditorProvider, EditorTooling, useTooling } from './lib/FabricJs';
import ToolBar from './components/Toolbar/Toolbar';
import FooterBar from './components/Footer/FooterBar';
import CanvasBody from './components/CanvasBody';
import PropertiesPanel from './components/Properties/PropertiesPanel';
import RecoveryDialog from './components/RecoveryDialog';
import { SaveProvider } from './lib/FabricJs/providers/SaveProvider';
import { editorTheme } from './theme';
import { RealSize } from './types';
import { UpdateSlideTitle } from '../../types/slide.types';
import { Socket } from 'socket.io-client';

const Body = styled.div`
  background: white;
  display: flex;
  flex-grow: 1;
  min-height: 0;
`;

const MainContainer = styled.div`
  background: ${editorTheme.workspace};
  display: flex;
  flex-grow: 1;
  min-width: 0;
  flex-direction: column;
  justify-content: space-between;
`;

// https://clubmate.fi/tabs#result-tabpanel-1

const EDITOR_CLASS_NAME = 'canvas-editor-v2';

const GlobalStyle = styled.main`
  height: 100vh;
  display: flex;
  flex-direction: column;
  * {
    font-family: 'roboto', sans-serif;
    box-sizing: border-box;
  }
`;

// Defaults only: :where() adds no specificity, so editor components can
// override them (a nested rule in GlobalStyle would outrank them).
const ButtonDefaults = createGlobalStyle`
  :where(.${EDITOR_CLASS_NAME} button) {
    border-radius: 4px;
    border: none;
    cursor: pointer;
  }
  :where(.${EDITOR_CLASS_NAME} button:active) {
    opacity: 0.7;
  }
`;

type CanvasEditorV2Props = RealSize & {
  onClickPreview: (json: object) => void;
  updateTitle?: UpdateSlideTitle;
  socket: Socket;
};

const CanvasEditorV2: FC<CanvasEditorV2Props> = ({
  realHeight,
  realWidth,
  onClickPreview,
  updateTitle,
  socket,
}) => {
  return (
    <EditorProvider>
      <SaveProvider>
        <ButtonDefaults />
        <GlobalStyle className={EDITOR_CLASS_NAME}>
          <Header
            socket={socket}
            onClickPreview={onClickPreview}
            updateTitle={updateTitle}
          />
          <Body>
            <SidePanel />
            <MainContainer>
              <ToolBar />
              <CanvasBody realHeight={realHeight} realWidth={realWidth} />
              <FooterBar />
            </MainContainer>
            <PropertiesPanel />
          </Body>
          <RecoveryDialog />
        </GlobalStyle>
      </SaveProvider>
    </EditorProvider>
  );
};

export default memo(CanvasEditorV2);

export { useTooling, EditorTooling };
